/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { api } from "core/api";
import { Table, SvgIcon, Card, Input } from "core/components";
import { ProjectFormData } from "core/schema";
import { change_order_items_admin_project_path } from "routes";
import formatter from "core/formatter";

type ProjectChangeOrderItemsProps = {
  project: ProjectFormData;
};

const ProjectChangeOrderItems = ({ project }: ProjectChangeOrderItemsProps): JSX.Element => {
  const [loading, setLoading] = React.useState(false);
  const [changeOrderItems, setChangeOrderItems] = React.useState([]);
  const [totalBudgetedAmount, setTotalBudgetedAmount] = React.useState(project.change_orders_total_budgeted_amount);
  const [totalActualIssuedAmount, setTotalActualIssuedAmount] = React.useState(project.change_orders_total_actual_cost);
  const [totalCostVariance, setTotalCostVariance] = React.useState(project.change_orders_total_cost_variance);
  const [isFiltered, setIsFiltered] = React.useState(false);
  const table = React.useRef(null);
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "ID",
        accessor: "change_order.code",
        Cell: (props) => {
          return <p className="text-center text-truncate">{props.value}</p>;
        }
      },
      {
        id: 2,
        Header: "Task",
        accessor: "task",
        Cell: (props) => {
          const task = props.value;

          return (
            <>
              <div className="text-bold text-truncate" title={task.task_classification.name} data-bs-toggle="tooltip">
                {`${task.code} - ${task.task_classification.name}`}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={task.name} data-bs-toggle="tooltip">
                {task.name}
              </p>
            </>
          );
        }
      },
      {
        id: 3,
        Header: "Item ID",
        accessor: "item.code",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 4,
        Header: "Item Name",
        accessor: "item.name",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 5,
        Header: "Task Code",
        accessor: "task.code",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Item",
        accessor: "item",
        Cell: (props) => {
          const item = props.value;

          return (
            <>
              <div className="text-bold" title={item.code} data-bs-toggle="tooltip">
                {item.code}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={item.name} data-bs-toggle="tooltip">
                {item.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Budgeted Quantity",
        accessor: "budgeted_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{`${props.value} ${props.row.original.unit_measure_code}`}</p>
            </div>
          );
        }
      },
      {
        Header: "Deducted Quantity",
        accessor: "deducted_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{`${props.value} ${props.row.original.unit_measure_code}`}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Unit Rate",
        accessor: "budgeted_unit_rate",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Amount",
        accessor: "budgeted_amount",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Actual Issued Quantity",
        accessor: "actual_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center text-bold">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Actual Issued Amount",
        accessor: "total_actual_cost",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right text-bold">{formatter.format(props.value || 0.0)}</p>
            </div>
          );
        }
      },
      {
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Variance",
        accessor: "cost_variance",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              {props.value < 0 ? (
                <p className="text-truncate text-right" style={{ color: "red" }}>{`(${Math.abs(parseFloat(props.value))?.toLocaleString("en-US", {
                  minimumFractionDigits: 2
                })})`}</p>
              ) : (
                <p className="text-truncate text-right text-bold">
                  {props.value ? parseFloat(props.value).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "-"}
                </p>
              )}
            </div>
          );
        }
      },
      {
        Header: "Open Requests",
        accessor: "open_requests",
        Cell: (props) => {
          const value = props.value;
          return (
            <p className="text-center text-bold" title={value ?? 0}>
              {value ?? 0}
            </p>
          );
        }
      }      
    ],
    []
  );

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Project Change Order Items...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No approved change order items yet.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input
            placeholder="Search for Change Order ID"
            onChange={(e) => {
              table.current.setFilter(1, e.target.value);
              setIsFiltered(!isFiltered);
            }}
          />
        </Col>
        <Col>
          <Input
            placeholder="Search for Task ID"
            onChange={(e) => {
              table.current.setFilter(5, e.target.value);
              setIsFiltered(!isFiltered);
            }}
          />
        </Col>
        <Col>
          <Input
            placeholder="Search for Item ID"
            onChange={(e) => {
              table.current.setFilter(3, e.target.value);
              setIsFiltered(!isFiltered);
            }}
          />
        </Col>
        <Col>
          <Input
            placeholder="Search for Item Name"
            onChange={(e) => {
              table.current.setFilter(4, e.target.value);
              setIsFiltered(!isFiltered);
            }}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={changeOrderItems} ref={table} hiddenColumns={[3, 4, 5]} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return changeOrderItems.length > 0 ? (
      <>
        {dataFilter()}
        {
          <Row>
            <Col></Col>
            <Col sm="auto">
              <span className="text-bold">Total Budgeted Amount: </span>
              <span>{formatter.format(totalBudgetedAmount)}</span>
            </Col>
            <Col sm="auto">
              <span className="text-bold">Total Actual Issued Amount: </span>
              <span>{formatter.format(totalActualIssuedAmount)}</span>
            </Col>
            <Col sm="auto">
              <span className="text-bold">Total Variances: </span>
              {totalCostVariance < 0 ? (
                <span style={{ color: "red" }}>{formatter.format(Math.abs(parseFloat(totalCostVariance)))}</span>
              ) : (
                <span>{totalCostVariance ? formatter.format(totalCostVariance) : "-"}</span>
              )}
            </Col>
          </Row>
        }
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  const getChangeOrderItems = async () => {
    try {
      setLoading(true);
      const response = await api.get(change_order_items_admin_project_path(project.id));
      setChangeOrderItems(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const reducer = (accumulator, currrent) => parseFloat(accumulator) + parseFloat(currrent);

  const calculateTotalBudgetedAmount = (data) => {
    const filteredBudgetedAmount = data.map((d) => +d.budgeted_amount);
    const totalBudgetedAmount = filteredBudgetedAmount.reduce(reducer, 0);

    setTotalBudgetedAmount(totalBudgetedAmount);

    return totalBudgetedAmount;
  };

  const calculateTotalActualIssuedAmount = (data) => {
    const filteredActualIssuedAmount = data.map((d) => +d.total_actual_cost);
    const totalActualIssuedAmount = filteredActualIssuedAmount.reduce(reducer, 0);

    setTotalActualIssuedAmount(totalActualIssuedAmount);

    return totalActualIssuedAmount;
  };

  React.useEffect(() => {
    getChangeOrderItems().catch(console.error);
  }, []);

  React.useEffect(() => {
    if (table.current) {
      const filteredData = table.current.filteredRows.map((d) => d.original);
      const totalBudgetedAmount = calculateTotalBudgetedAmount(filteredData);
      const totalActualIssuedAmount = calculateTotalActualIssuedAmount(filteredData);
      setTotalCostVariance(totalActualIssuedAmount - totalBudgetedAmount);
    }
  }, [isFiltered]);

  return (
    <React.StrictMode>
      <Container fluid={true}>
        <Row>
          <Col>
            <Card.Body>{loading ? loader() : content()}</Card.Body>
          </Col>
        </Row>
      </Container>
    </React.StrictMode>
  );
};

export default ProjectChangeOrderItems;
